import { useCallback, useEffect, useState } from 'react'

import { useWallet } from '@tronweb3/tronwallet-adapter-react-hooks'

import { analyticsService } from '@src/services/analytics'
import { TronAddress } from '@src/types/token'
import { logger } from '@src/utils/logger'

export const useTronWallet = () => {
    const [isTronConnected, setIsTronConnected] = useState<boolean>(false)
    const [tronWalletAddress, setTronWalletAddress] = useState<TronAddress | undefined>(undefined)

    const { connected: tronWalletConnected, address: tronAddress, select, connect, disconnect, wallet } = useWallet()

    // Track connection attempts and failures
    const connectWallet = useCallback(
        async (walletName: string) => {
            try {
                // Track the specific wallet connection attempt
                analyticsService.trackSpecificWalletConnectionAttempt(
                    walletName || 'TronLink', // Default to TronLink if no specific name provided
                    'TRX'
                )

                // We need to use a type assertion here since the Tron wallet adapter expects a specific type
                // This is safe because the walletName will be validated by the adapter
                // @ts-ignore - The AdapterName type is not directly accessible
                select(walletName as AdapterName)

                // Connect to the wallet
                await connect()

                logger.info('[TronWallet] Connected successfully', { walletName })
                return true
            } catch (error) {
                // Track the wallet connection failure
                analyticsService.trackWalletConnectionFailed(
                    error,
                    walletName || 'TronLink', // Default to TronLink if no specific name provided
                    'TRX'
                )

                logger.error('[TronWallet] Connection failed', { error, walletName })
                return false
            }
        },
        [select, connect]
    )

    useEffect(() => {
        if (tronWalletConnected && tronAddress) {
            setIsTronConnected(true)
            setTronWalletAddress(tronAddress as TronAddress)

            // Explicitly track the successful wallet connection for Tron wallets
            // This ensures the wallet_connected event fires consistently for all wallet types
            const walletName = wallet?.adapter?.name || 'TronLink'
            analyticsService.trackWalletConnect(tronAddress, 'TRX', walletName)
            logger.info('[TronWallet] Tracked successful wallet connection', { address: tronAddress, walletName })
        } else {
            setIsTronConnected(false)
            setTronWalletAddress(undefined)

            // Track wallet disconnect for analytics
            const lastAddress = tronAddress as string | undefined
            const walletName = wallet?.adapter?.name || 'TronLink'
            analyticsService.trackWalletDisconnect(lastAddress, 'TRX', walletName)
        }
    }, [tronWalletConnected, tronAddress])

    return {
        isTronConnected,
        tronWalletAddress,
        connectTronWallet: connectWallet,
        disconnectTronWallet: disconnect,
        currentWallet: wallet,
    }
}
